import { useEffect, useState } from 'react'
import { apiJson, getActiveTenantId } from './api'

// Per-person settings kept on the server (routes/preferences.js), so they
// follow the viewer to any machine they sign in on (§7: the dashboard layout
// "is saved against you"; §7n.19: a dismissed help panel stays dismissed for
// that person). One GET per person per workspace, shared by every screen;
// a write updates the cache at once and is sent behind it.

interface TokenSource {
  getIdToken: () => Promise<string>
  uid?: string
}

interface Entry {
  values: Record<string, unknown>
  ready: boolean // the first read has landed (or failed)
  failed: boolean // it failed: the next screen to mount asks again
  loading: boolean
}

const cache = new Map<string, Entry>()
const listeners = new Set<() => void>()

function scope(user: TokenSource | null | undefined) {
  return user ? `${user.uid || '?'}|${getActiveTenantId() || ''}` : null
}

function notify() {
  for (const l of listeners) l()
}

function entryFor(sk: string): Entry {
  let e = cache.get(sk)
  if (!e) {
    e = { values: {}, ready: false, failed: false, loading: false }
    cache.set(sk, e)
  }
  return e
}

function load(user: TokenSource, sk: string) {
  const e = entryFor(sk)
  if (e.loading || (e.ready && !e.failed)) return
  e.loading = true
  apiJson<{ preferences: Record<string, unknown> }>(user, '/api/me/preferences')
    .then((r) => {
      // A choice made while the read was in flight wins over what it read.
      e.values = { ...(r?.preferences || {}), ...e.values }
      e.failed = false
    })
    .catch(() => {
      // The screen shows its defaults; nothing is remembered as read.
      e.failed = true
    })
    .finally(() => {
      e.loading = false
      e.ready = true
      notify()
    })
}

// Tests start each case with nothing loaded.
export function clearPreferenceCache() {
  cache.clear()
}

// [value, set, loaded]. Until the first read lands `value` is the fallback
// and `loaded` is false, so a screen can hold back something (a help panel)
// that would otherwise flash open for a person who closed it.
export function usePreference<T>(user: TokenSource | null | undefined, key: string, fallback: T): [T, (v: T) => void, boolean] {
  const sk = scope(user)
  const [, bump] = useState(0)
  useEffect(() => {
    const l = () => bump((n) => n + 1)
    listeners.add(l)
    return () => {
      listeners.delete(l)
    }
  }, [])
  useEffect(() => {
    if (user && sk) load(user, sk)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sk])

  const e = sk ? cache.get(sk) : undefined
  const has = !!e && Object.prototype.hasOwnProperty.call(e.values, key)
  const value = has ? (e!.values[key] as T) : fallback
  const loaded = !user || !!e?.ready

  const set = (v: T) => {
    if (!user || !sk) return
    const entry = entryFor(sk)
    entry.values = { ...entry.values, [key]: v }
    notify()
    apiJson(user, '/api/me/preferences/' + key, { method: 'PUT', body: { value: v } }).catch(() => {
      /* kept for this session; the next sign-in reads what the server has */
    })
  }
  return [value, set, loaded]
}

// A help panel (§7n.19): open until this person closes it. Called with a
// boolean it sets; with anything else (a click event) it toggles.
export function useHelpPref(user: TokenSource | null | undefined, panel: string): [boolean, (next?: unknown) => void] {
  const [on, set, loaded] = usePreference<boolean>(user, 'help.' + panel, true)
  const change = (next?: unknown) => set(typeof next === 'boolean' ? next : !on)
  return [loaded && on, change]
}
